import React, { useState, useRef, useMemo } from "react";
import { 
  TextField, 
  Popper, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Typography, 
  Box,
  useTheme,
  ClickAwayListener
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  popper: {
    zIndex: 1300,
    width: 700,
    maxWidth: "min(700px, calc(100vw - 32px))",
    marginTop: theme.spacing(1),
  },
  suggestionPaper: {
    maxHeight: 300,
    overflow: "auto",
    overflowX: "hidden",
    boxShadow: theme.palette.mode === "dark" 
      ? "0px 8px 24px rgba(0, 0, 0, 0.4)" 
      : "0px 8px 24px rgba(0, 0, 0, 0.12)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
    borderRadius: 8,
    width: "100%",
  },
  suggestionItem: {
    cursor: "pointer",
    padding: "10px 16px",
    minHeight: "auto",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" 
        ? "rgba(255, 255, 255, 0.08)" 
        : "rgba(0, 0, 0, 0.04)",
    },
  },
  maskDescription: {
    fontSize: "0.7rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  maskCode: {
    fontSize: "0.7rem",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontFamily: "monospace",
    marginLeft: 8,
  },
  categoryBadge: {
    display: "inline-block",
    fontSize: "0.65rem",
    padding: "2px 6px",
    borderRadius: 4,
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    color: theme.palette.text.secondary,
    marginLeft: "auto",
  },
}));

// ✅ PADRONIZADO: Todas as máscaras agora usam {{variavel}}
const AVAILABLE_MASKS = [
  // === CONTATO ===
  { code: "{{firstName}}", description: "Primeiro Nome", category: "Contato" },
  { code: "{{name}}", description: "Nome Completo", category: "Contato" },
  { code: "{{contactNumber}}", description: "Número do Contato", category: "Contato" },
  { code: "{{contactEmail}}", description: "Email do Contato", category: "Contato" },
  // Aliases para compatibilidade com campanhas
  { code: "{{nome}}", description: "Nome do Contato (Campanha)", category: "Contato" },
  { code: "{{numero}}", description: "Número do Contato (Campanha)", category: "Contato" },
  { code: "{{email}}", description: "Email do Contato (Campanha)", category: "Contato" },
  // Variável de idade para aniversários
  { code: "{{idade}}", description: "Idade do Contato", category: "Contato" },
  
  // === TICKET ===
  { code: "{{ticket_id}}", description: "ID do Ticket", category: "Ticket" },
  { code: "{{ticketUuid}}", description: "UUID do Ticket", category: "Ticket" },
  { code: "{{ticketStatus}}", description: "Status do Ticket", category: "Ticket" },
  { code: "{{ticketDate}}", description: "Data de Criação do Ticket", category: "Ticket" },
  { code: "{{ticketTime}}", description: "Hora de Criação do Ticket", category: "Ticket" },
  { code: "{{ticketDateTime}}", description: "Data e Hora de Criação", category: "Ticket" },
  
  // === USUÁRIO/ATENDENTE ===
  { code: "{{userName}}", description: "Nome do Atendente", category: "Usuário" },
  { code: "{{userId}}", description: "ID do Atendente", category: "Usuário" },
  { code: "{{userEmail}}", description: "Email do Atendente", category: "Usuário" },
  
  // === FILA/SETOR ===
  { code: "{{queue}}", description: "Nome da Fila", category: "Fila" },
  { code: "{{queueId}}", description: "ID da Fila", category: "Fila" },
  { code: "{{position}}", description: "Posição na Fila", category: "Fila" },
  
  // === CONEXÃO ===
  { code: "{{connection}}", description: "Nome da Conexão", category: "Conexão" },
  { code: "{{connectionId}}", description: "ID da Conexão", category: "Conexão" },
  { code: "{{connectionNumber}}", description: "Número da Conexão", category: "Conexão" },
  
  // === EMPRESA ===
  { code: "{{name_company}}", description: "Nome da Empresa", category: "Empresa" },
  { code: "{{companyId}}", description: "ID da Empresa", category: "Empresa" },
  { code: "{{companyPhone}}", description: "Telefone da Empresa", category: "Empresa" },
  { code: "{{companyEmail}}", description: "Email da Empresa", category: "Empresa" },
  
  // === SISTEMA ===
  { code: "{{ms}}", description: "Saudação", category: "Sistema" },
  { code: "{{protocol}}", description: "Protocolo", category: "Sistema" },
  { code: "{{date}}", description: "Data Atual", category: "Sistema" },
  { code: "{{hour}}", description: "Hora Atual", category: "Sistema" },
  // === AGENDAMENTO ===
  { code: "{{dataAgendamento}}", description: "Data do Agendamento", category: "Agendamento" },
];

const MaskInput = ({ field, form, value, onChange, label, multiline, rows, required, helperText, showHelperText = true, ...props }) => {
  // ✅ Suporte para Formik e inputs normais
  const finalValue = field ? (field.value ?? "") : (value ?? "");

  const classes = useStyles();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filteredMasks, setFilteredMasks] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const textFieldRef = useRef(null);

  const handleInputRef = React.useCallback((ref) => {
    inputRef.current = ref;
    if (typeof props.inputRef === "function") {
      props.inputRef(ref);
    } else if (props.inputRef && typeof props.inputRef === "object") {
      props.inputRef.current = ref;
    }
  }, [props.inputRef]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    setCursorPosition(cursorPos);
    
    if (form && field?.name) {
      form.setFieldValue(field.name, newValue);
    }
    if (field?.onChange) {
      field.onChange(e);
    }
    if (onChange) {
      onChange(e);
    }

    // Verificar se o usuário digitou "@" para abrir o menu de máscaras
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpaceAfterAt = textAfterAt.includes(" ") || textAfterAt.includes("\n");
      
      if (!hasSpaceAfterAt) {
        const searchTerm = textAfterAt.toLowerCase();
        setSearchText(searchTerm);
        
        const filtered = AVAILABLE_MASKS.filter(mask => 
          mask.code.toLowerCase().includes(searchTerm) || 
          mask.description.toLowerCase().includes(searchTerm) ||
          mask.category.toLowerCase().includes(searchTerm)
        );
        
        setFilteredMasks(filtered);
        setSelectedIndex(0);
        
        setTimeout(() => {
          if (textFieldRef.current) {
            const inputElement = textFieldRef.current.querySelector('input') || 
                                 textFieldRef.current.querySelector('textarea');
            if (inputElement) {
              setAnchorEl(inputElement);
            } else {
              setAnchorEl(textFieldRef.current);
            }
          }
        }, 50);
      } else {
        if (anchorEl) setAnchorEl(null);
        if (filteredMasks.length > 0) setFilteredMasks([]);
        if (selectedIndex !== 0) setSelectedIndex(0);
      }
    } else {
      if (anchorEl) setAnchorEl(null);
      if (filteredMasks.length > 0) setFilteredMasks([]);
      if (selectedIndex !== 0) setSelectedIndex(0);
    }
  };

  const handleMaskSelect = (mask) => {
    if (!inputRef.current) return;

    const currentValue = finalValue || "";
    const textBeforeCursor = currentValue.substring(0, cursorPosition);
    const textAfterCursor = currentValue.substring(cursorPosition);
    
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex !== -1) {
      const newText = 
        currentValue.substring(0, lastAtIndex) + 
        mask.code + 
        " " + 
        textAfterCursor;
      
      const fieldName = field?.name || props.name || "maskInput";
      const syntheticEvent = {
        target: {
          value: newText,
          name: fieldName,
        },
      };
      
      if (form && fieldName) {
        form.setFieldValue(fieldName, newText);
      }
      if (field?.onChange) {
        field.onChange(syntheticEvent);
      }
      if (onChange) {
        onChange(syntheticEvent);
      }
      
      setAnchorEl(null);
      setFilteredMasks([]);
      setSearchText("");
      setSelectedIndex(0);
      
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = lastAtIndex + mask.code.length + 1;
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (anchorEl && filteredMasks.length > 0) {
      if (e.key === "Escape") {
        setAnchorEl(null);
        setFilteredMasks([]);
        setSelectedIndex(0);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredMasks.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredMasks.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleMaskSelect(filteredMasks[selectedIndex]);
      }
    }
  };

  const open = Boolean(anchorEl) && filteredMasks.length > 0;
  const finalHelperText = helperText || (showHelperText ? "💡 Digite @ para inserir variáveis dinâmicas" : "");

  const handleCloseModal = () => {
    setAnchorEl(null);
    setFilteredMasks([]);
    setSelectedIndex(0);
  };

  // Se multiline for verdadeiro, remove type customizado do props
  const { type, inputRef: propInputRef, ...cleanProps } = props;
  const inputType = multiline ? undefined : type;

  return (
    <>
      <TextField
        {...cleanProps}
        type={inputType}
        name={field?.name || cleanProps.name}
        inputRef={handleInputRef}
        ref={textFieldRef}
        value={finalValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        label={label}
        multiline={multiline}
        rows={rows}
        required={required}
        helperText={finalHelperText}
        InputProps={{
          ...cleanProps.InputProps,
        }}
      />
      {open && (
        <ClickAwayListener onClickAway={handleCloseModal}>
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            className={classes.popper}
            modifiers={{
              offset: {
                enabled: true,
                offset: "0, 8",
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: "viewport",
              },
            }}
            disablePortal={false}
            style={{ width: 700, maxWidth: "min(700px, calc(100vw - 32px))" }}
          >
            <Paper className={classes.suggestionPaper} elevation={8}>
              <List dense style={{ padding: 0 }}>
                {filteredMasks.map((mask, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleMaskSelect(mask)}
                    className={classes.suggestionItem}
                    style={{
                      backgroundColor: selectedIndex === index
                        ? (theme.palette.mode === "dark" 
                            ? "rgba(255, 255, 255, 0.12)" 
                            : "rgba(0, 0, 0, 0.06)")
                        : "transparent",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", flex: 1, minWidth: 0 }}>
                            <Typography className={classes.maskDescription} style={{ whiteSpace: "nowrap", marginRight: 8 }}>
                              {mask.description}
                            </Typography>
                            <Typography className={classes.maskCode} style={{ whiteSpace: "nowrap" }}>
                              {mask.code}
                            </Typography>
                          </Box>
                          <Typography className={classes.categoryBadge} style={{ flexShrink: 0 }}>
                            {mask.category}
                          </Typography>
                        </Box>
                      }
                      style={{ margin: 0, padding: 0, width: "100%" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default MaskInput;
